import { PersistedPendingRegion, RegionData } from "../types";
import { StreamingBackendFallbackReason } from "./PlaybackBackend";

export interface StreamingBackendSelectionOptions {
  streamingAudioEngineEnabled: boolean;
  browserSupportsStreamingPlayback: boolean;
  regions: Pick<RegionData, "codec" | "audioBlob">[];
  pendingRegions?: Pick<PersistedPendingRegion, "id">[];
}

export interface StreamingBackendSelectionDecision {
  fallbackReason: StreamingBackendFallbackReason | null;
  useStreamingBackend: boolean;
}

export function supportsStreamingPlayback(
  audioContext: Pick<AudioContext, "audioWorklet">
): boolean {
  const sharedArrayBufferSupported = typeof SharedArrayBuffer !== "undefined";
  const audioWorkletSupported =
    typeof AudioWorkletNode !== "undefined" &&
    typeof audioContext.audioWorklet?.addModule === "function";
  const crossOriginIsolatedSupported =
    (globalThis as typeof globalThis & { crossOriginIsolated?: boolean })
      .crossOriginIsolated === true;

  return (
    sharedArrayBufferSupported &&
    audioWorkletSupported &&
    crossOriginIsolatedSupported
  );
}

export function getStreamingBackendSelectionDecision(
  options: StreamingBackendSelectionOptions
): StreamingBackendSelectionDecision {
  const hasPendingRegions =
    (options.pendingRegions?.length ?? 0) > 0 ||
    options.regions.some((region) => region.audioBlob instanceof Blob);

  if (!options.streamingAudioEngineEnabled) {
    return { fallbackReason: "flag_off", useStreamingBackend: false };
  }

  if (!options.browserSupportsStreamingPlayback) {
    return {
      fallbackReason: "browser_unsupported",
      useStreamingBackend: false,
    };
  }

  if (hasPendingRegions) {
    return {
      fallbackReason: "pending_regions_present",
      useStreamingBackend: false,
    };
  }

  const supportedCodecs = new Set(["opus", "mp3"])
  if (options.regions.some((region) => !region.codec || !supportedCodecs.has(region.codec))) {
    return { fallbackReason: "unsupported_codec", useStreamingBackend: false };
  }

  return { fallbackReason: null, useStreamingBackend: true };
}

export function shouldUseStreamingBackend(
  options: StreamingBackendSelectionOptions
): boolean {
  return getStreamingBackendSelectionDecision(options).useStreamingBackend;
}
